import { UserRejectedRequestError } from 'viem';
import {
    useAccount,
    useConnect,
    useDisconnect,
    useSendTransaction,
    useSignMessage,
    useSignTypedData,
    useWalletClient,
} from 'wagmi';
import { goerli, mainnet, polygon } from 'wagmi/chains';

import { useAppDispatch } from '@pkg/hooks';
import { Web3Chains, Web3WagmiConnectors } from '@pkg/providers/Web3Provider/types';
import { getConnector } from '@pkg/providers/Web3Provider/utilities';
import { type WagmiCallbacks } from '@pkg/types';

import config from '@app/config';
import { addNotification } from '@app/store/actions/notification';

export interface WalletConnectOptions extends WagmiCallbacks {}

export const useWalletConnect = ({ onError }: WalletConnectOptions = {}) => {
    const { connectAsync: wagmiConnectAsync, connectors } = useConnect();
    const { disconnectAsync } = useDisconnect();

    const { signMessageAsync: wagmiSignMessageAsync } = useSignMessage({ onError });
    const { signTypedDataAsync: wagmiSignTypedDataAsync } = useSignTypedData({ onError });
    const { sendTransaction: wagmiSendTransaction } = useSendTransaction({ onError });

    const dispatch = useAppDispatch();

    const { address, isConnected, connector } = useAccount();
    const walletClient = useWalletClient();

    function parseErrorCode(errorMessage: string): string {
        return errorMessage.substring(0, errorMessage.indexOf(':'));
    }

    function handleError(errorMessage: string) {
        const errorName = parseErrorCode(errorMessage);
        switch (errorName) {
            case UserRejectedRequestError.name: {
                dispatch(addNotification({ message: 'You rejected the request in your wallet.', severity: 'error' }));
            }
        }
    }

    async function signMessageAsync(...args) {
        try {
            return await wagmiSignMessageAsync(...args);
        } catch (error) {
            handleError(String(error));
        }
    }

    async function signTypedDataAsync(...args) {
        try {
            return await wagmiSignTypedDataAsync(...args);
        } catch (error) {
            handleError(String(error));
        }
    }

    async function sendTransaction(...args) {
        try {
            return await wagmiSendTransaction(...args);
        } catch (error) {
            handleError(String(error));
        }
    }

    const connectAsync = async (type: Web3WagmiConnectors, blockchain: Web3Chains = Web3Chains.Ethereum) => {
        try {
            let account: string;

            let chain: number;
            switch (blockchain) {
                case Web3Chains.Ethereum:
                    chain = config.nodeEnv === 'production' ? mainnet.id : goerli.id;
                    break;
                case Web3Chains.Polygon:
                    chain = polygon.id;
                    break;
                default:
                    chain = mainnet.id;
                    break;
            }

            if (type === Web3WagmiConnectors.Metamask) {
                const result = await wagmiConnectAsync({
                    connector: getConnector(connectors, Web3WagmiConnectors.Metamask),
                    chainId: 1,
                });
                account = result.account;
            } else if (type === Web3WagmiConnectors.Ledger) {
                const result = await wagmiConnectAsync({
                    connector: getConnector(connectors, Web3WagmiConnectors.Ledger),
                    chainId: chain,
                });
                account = result.account;
            } else {
                const result = await wagmiConnectAsync({
                    connector: getConnector(connectors, Web3WagmiConnectors.WalletConnect),
                });
                account = result.account;
            }
            return account;
        } catch {
            return '';
        }
    };

    return {
        connectAsync,
        connector,
        currentAccount: address?.toLowerCase(),
        disconnectAsync,
        isConnected: isConnected,
        sendTransaction,
        signMessageAsync,
        signTypedDataAsync,
        walletClient,
    };
};
